import React, { useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { useAppContext } from '~/app-context'
import { FileDropOverlayLogic, type FileDropOverlayDeps } from './logic'
import { LoadingIndicator } from '~common/components/loading'
import { SimpleProgressBar } from '~common/components/progress'

export interface FileDropOverlayProps {}

export const FileDropOverlay: React.FC<FileDropOverlayProps> = () => {
    const appContext = useAppContext()
    const { logic, state } = useLogic<
        FileDropOverlayLogic,
        FileDropOverlayDeps
    >(
        FileDropOverlayLogic,
        {
            ...appContext,
        },
        undefined,
    )

    const containerRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        const handleDragOver = (e: DragEvent) => {
            // Prevent browser from opening the file
            e.preventDefault()
        }
        const handleDragEnter = (e: DragEvent) => {
            logic.onGlobalDragEnter(e)
        }
        const handleDragLeave = (e: DragEvent) => {
            logic.onGlobalDragLeave(e)
        }
        const handleDrop = async (e: DragEvent) => {
            e.preventDefault()
            const files = Array.from(e.dataTransfer?.files || [])
            if (files.length > 0) {
                await logic.processFiles(files)
            }
            logic.onGlobalDrop()
        }

        window.addEventListener('dragover', handleDragOver)
        window.addEventListener('dragenter', handleDragEnter)
        window.addEventListener('dragleave', handleDragLeave)
        window.addEventListener('drop', handleDrop)

        return () => {
            window.removeEventListener('dragover', handleDragOver)
            window.removeEventListener('dragenter', handleDragEnter)
            window.removeEventListener('dragleave', handleDragLeave)
            window.removeEventListener('drop', handleDrop)
        }
    }, [logic])

    useEffect(() => {
        const unsubscribe = appContext.services.globalEvents.listen((event) => {
            if (event.type === 'showProcessingOverlay') {
                logic.showProcessing(event.label as string | undefined)
            }
            if (event.type === 'hideProcessingOverlay') {
                logic.hideProcessing()
            }
        })
        return unsubscribe
    }, [appContext.services.globalEvents, logic])

    const onDropToZone = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation() // Prevent event from bubbling to window handler
        const files = Array.from(e.dataTransfer?.files || [])
        if (files.length === 0) return
        await logic.processFiles(files)
    }

    const onDragOverZone = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
    }

    const progressPercentage =
        state.batchTotalFiles > 1
            ? Math.round(
                  ((state.batchCurrentIndex + 1) / state.batchTotalFiles) * 100,
              )
            : 0

    const progressLabel =
        state.batchTotalFiles > 1
            ? `Processing ${state.batchCurrentIndex + 1} of ${state.batchTotalFiles} files`
            : (state.processingLabel ?? 'Processing invoice...')

    return (
        <>
            {state.isDraggingFiles && !state.processingVisible && (
                <OverlayContainer ref={containerRef}>
                    <DropZone
                        position="bottom"
                        onDrop={onDropToZone}
                        onDragOver={onDragOverZone}
                    >
                        <ZoneLabel>Drop to Import Invoice</ZoneLabel>
                        <ZoneSubLabel>
                            Or drop on a transaction to instant-match
                        </ZoneSubLabel>
                    </DropZone>
                </OverlayContainer>
            )}
            {state.processingVisible && (
                <ProgressBarContainer>
                    <BatchProgressOverlay>
                        <BatchProgressContent>
                            <BatchProgressLabel>
                                {progressLabel}
                            </BatchProgressLabel>
                            <SimpleProgressBar
                                value={progressPercentage}
                                type="round"
                            />
                        </BatchProgressContent>
                    </BatchProgressOverlay>
                </ProgressBarContainer>
            )}
        </>
    )
}

const OverlayContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 9999;
`

const DropZone = styled.div<{ position: 'top' | 'bottom' }>`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
    position: absolute;

    left: ${({ theme }) => theme.spacing[4]};
    right: ${({ theme }) => theme.spacing[4]};
    bottom: ${({ theme }) => theme.spacing[12]};
    height: 20%;
    pointer-events: auto; /* Capture events only in zones */
    display: flex;
    margin: ${({ theme }) => theme.spacing[4]};
    box-sizing: border-box;
    align-items: center;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    justify-content: center;
    border: 2px dashed ${({ theme }) => theme.colors.info};
    background: ${({ theme }) => theme.colors.info}33;
    backdrop-filter: blur(4px);
    color: ${({ theme }) => theme.colors.text.primary};
    ${({ position }) =>
        position === 'top'
            ? css`
                  top: 0;
              `
            : css`
                  bottom: 0;
              `}
`

const ZoneLabel = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
    text-align: center;
    color: ${({ theme }) => theme.colors.info};
`
const ZoneSubLabel = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize['lg']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-align: center;
`

const ProgressBarContainer = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 9999;
    pointer-events: none;
    background: ${({ theme }) => theme.colors.background.bg1}90;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
`

const BatchProgressOverlay = styled.div`
    pointer-events: auto;
    background: ${({ theme }) => theme.colors.background.bg2};
    border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
    padding: ${({ theme }) => theme.spacing[4]};
    box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
`

const BatchProgressContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
    max-width: 800px;
    margin: 0 auto;
`

const BatchProgressLabel = styled.div`
    text-align: center;
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.text.primary};
    width: 100%;
`

export default FileDropOverlay
